import type { Contact } from '../types/contacts.types';

export type ChannelStatusLabel = 'Subscribed' | 'Pending' | 'Unsubscribed' | 'Suppressed';

export type ChannelStatusTone = 'success' | 'warning' | 'neutral' | 'error';

const CHANNEL_STATUS_TONES: Record<ChannelStatusLabel, ChannelStatusTone> = {
  Subscribed: 'success',
  Pending: 'warning',
  Unsubscribed: 'neutral',
  Suppressed: 'error',
};

const normalizeRawStatus = (value?: string | null): string => {
  const normalized = value?.trim().toLowerCase();
  if (!normalized) return '';
  return normalized;
};

/** Maps legacy/blended statuses to the 4-value per-channel model (prototype mock-store). */
export const collapseChannelStatus = (status: string): ChannelStatusLabel => {
  const normalized = status.trim();
  if (
    normalized === 'Bounced' ||
    normalized === 'Complaint' ||
    normalized === 'Suppressed' ||
    normalized === 'Blocked'
  ) {
    return 'Suppressed';
  }
  if (normalized === 'Subscribed' || normalized === 'Pending' || normalized === 'Unsubscribed') {
    return normalized;
  }
  const lower = normalized.toLowerCase();
  if (lower === 'subscribed') return 'Subscribed';
  if (lower === 'pending') return 'Pending';
  if (lower === 'unsubscribed') return 'Unsubscribed';
  if (lower === 'suppressed' || lower === 'bounced' || lower === 'complaint') return 'Suppressed';
  return 'Unsubscribed';
};

export const formatChannelStatusLabel = (value?: string | null): ChannelStatusLabel | null => {
  const normalized = normalizeRawStatus(value);
  if (!normalized) return null;
  return collapseChannelStatus(normalized);
};

export const contactHasPhone = (contact: Contact | undefined): boolean => {
  const phone = contact?.Phone?.trim();
  return Boolean(phone && phone !== '—');
};

export const getEmailChannelStatus = (contact: Contact | undefined): ChannelStatusLabel | null => {
  if (!contact) return null;
  // Registry flag takes precedence over subscription status; do not mutate status fields.
  // Blacklist / BlacklistedBy is not a suppression overlay — only EmailSuppressed === true.
  if (contact.EmailSuppressed === true) return 'Suppressed';
  return formatChannelStatusLabel(contact.EmailSubscriptionStatus) ?? 'Subscribed';
};

export const getSmsChannelStatus = (contact: Contact | undefined): ChannelStatusLabel | null => {
  if (!contact || !contactHasPhone(contact)) return null;
  // Registry flag takes precedence over subscription status; do not mutate status fields.
  // Blacklist / BlacklistedBy is not a suppression overlay — only SmsSuppressed === true.
  if (contact.SmsSuppressed === true) return 'Suppressed';
  return formatChannelStatusLabel(contact.SmsSubscriptionStatus) ?? 'Unsubscribed';
};

export const getChannelStatusTone = (status: ChannelStatusLabel): ChannelStatusTone =>
  CHANNEL_STATUS_TONES[status];

export const getChannelStatusPillClassName = (status: ChannelStatusLabel): string => {
  const tone = getChannelStatusTone(status);
  if (tone === 'success') return 'bg-emerald-50 text-emerald-800';
  if (tone === 'warning') return 'bg-amber-50 text-amber-800';
  if (tone === 'error') return 'bg-red-50 text-red-700';
  return 'bg-neutral-100 text-neutral-600';
};

export const getChannelStatusDotClassName = (status: ChannelStatusLabel): string => {
  const tone = getChannelStatusTone(status);
  if (tone === 'success') return 'bg-emerald-600';
  if (tone === 'warning') return 'bg-amber-600';
  if (tone === 'error') return 'bg-red-600';
  return 'bg-neutral-400';
};
